<template>
  <section class="[ max-w-7xl m-auto min-h-full flex flex-col ]">
    <div
      class="[ flex-1 flex flex-col items-center justify-center ]"
      v-if="apiState === ILoadingState.LOADING"
    >
      <JBLoadingSpinner message="Loading your dashboard" />
    </div>
    <div v-else class="[ grid grid-cols-[65%_auto] gap-10 pb-6">
      <div class="[ flex flex-col gap-8 ]">
        <div class="[ flex gap-6 justify-between ]">
          <CurrentUser show-name size="xlarge">
            <template #default>Welcome back</template>
          </CurrentUser>

          <div class="[ flex gap-3 items-start ]">
            <Permission type="User.Write.TeamManager">
              <RouterLink to="/users?new">
                <ButtonComponent outlined label="Invite">
                  <template #icon>
                    <JBIcon name="user-add" class="[ text-gray-dark ]" />
                  </template>
                </ButtonComponent>
              </RouterLink>
            </Permission>
            <Permission type="Demo.Write">
              <DemoUploadButton />
            </Permission>
          </div>
        </div>

        <HighlightCard>
          <template #title>
            <div class="[ flex items-center justify-between ]">
              <div class="[ flex gap-2.5 items-center ]">
                <span>Stats</span>
                <div v-tooltip="'Lorem ipsum dolor sit amet'">
                  <JBIcon
                    name="info-circle"
                    class="[ w-3 h-3 text-sm text-gray-dark ]"
                  />
                </div>
              </div>
              <DateRangeRadio
                :range-options="dateRanges"
                :intervals="dateRangeIntervals"
                :show-custom-datepicker="hasSelectedCustomDateRange"
                :custom-date-range="customDateRange"
                v-model="dateRangeIndex"
                @custom-date-selected="setCustomDateFilter"
              />
            </div>
          </template>
          <template #content>
            <StatsSummary :stats="contactsStats" :loading="loadingStats" />
          </template>
        </HighlightCard>

        <Card>
          <template #title>
            <div class="[ flex items-center justify-between ]">
              <div class="[ flex gap-2.5 items-center ]">
                <span>Performance</span>
                <div
                  v-tooltip="
                    'This graph shows the total number of calls, calls per agent and average QA score'
                  "
                >
                  <JBIcon
                    name="info-circle"
                    class="[ w-3 h-3 text-sm text-gray-dark ]"
                  />
                </div>
              </div>
              <DateRangeRadio
                :range-options="dateRanges"
                :intervals="dateRangeIntervals"
                :show-custom-datepicker="hasSelectedCustomDateRange"
                :custom-date-range="customDateRange"
                v-model="dateRangeIndex"
                @custom-date-selected="setCustomDateFilter"
              />
            </div>
          </template>
          <template #content>
            <JBChart
              type="line"
              :data="chartData"
              :options="chartOptions"
              :loading="loadingTimeseries"
              :class="{ 'opacity-50': loadingTimeseries }"
            />
          </template>
        </Card>

        <Card>
          <template #title> List snapshot </template>
          <template #content>
            <div class="[ flex flex-col -ml-5 -mr-5 ]">
              <ContactHistoryTable
                :page-size="5"
                no-paginator
                :columns="[
                  'agent',
                  'customer',
                  'status',
                  'rating',
                  'view_button',
                ]"
                :filters="{
                  customerPhoneQuery: '',
                  ratingRange: [0, 1],
                  teams: [],
                  users: [],
                }"
                :date-range="dateRange"
                @view-contact="onViewContactClicked"
              />
              <RouterLink
                class="[ flex flex-col mt-3 mx-5 ]"
                to="/calls/call-history"
              >
                <ButtonComponent outlined rounded> All calls </ButtonComponent>
              </RouterLink>
            </div>
          </template>
        </Card>
      </div>
      <div class="[ flex flex-col gap-4 ]">
        <Permission type="Team.Read.TeamManager">
          <Card class="[ mb-1 ]">
            <template #title> Leaderboard </template>
            <template #content>
              <LeaderboardSummary />
            </template>
          </Card>
        </Permission>

        <TeamCard :team="team" v-for="team in allTeams" :key="team.id" />
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import { Button as ButtonComponent, Card } from "primevue";
import CurrentUser from "@/components/auth/CurrentUser.vue";
import HighlightCard from "@/components/ui/HighlightCard.vue";
import LeaderboardSummary from "@/components/agents/LeaderboardSummary.vue";
import StatsSummary from "@/components/ui/StatsSummary.vue";
import ContactHistoryTable from "@/components/contact/qa/ContactHistoryTable.vue";
import DateRangeRadio from "@/components/contact/qa/DateRangeRadio.vue";
import Permission from "@/components/auth/Permission.vue";

import TeamCard from "@/components/agents/team/TeamCard.vue";
import { computed, onMounted, ref } from "vue";
import { ContactFilters, useTeamStore } from "@/store/admin/team.ts";
import { ILoadingState } from "@/utils/types.ts";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import DemoUploadButton from "@/components/contact/qa/DemoUploadButton.vue";
import { useContactsStats } from "@/composables/contacts-stats.ts";
import { useContactsTimeseries } from "@/composables/contacts-timeseries.ts";
import JBChart from "@/components/ui/JBChart.vue";
import {
  dateIntervals,
  dateRangeIntervals,
  dateRanges,
  getDateIntervalForCustomDateRange,
} from "@/utils/contacts/filters.ts";
import { useContactTimeseriesChart } from "@/utils/ui/charts/contacts-timeseries.ts";

definePage({
  meta: {
    title: "Dashboard",
    icon: "home",
  },
});

const router = useRouter();
const apiState = ref<ILoadingState>(ILoadingState.IDLE);
const teamStore = useTeamStore();
const { allTeams } = storeToRefs(teamStore);

const dateRangeIndex = ref(0);
const customDateRange = ref<[Date | null, Date | null]>([null, null]);

const hasSelectedCustomDateRange = computed<boolean>(() => {
  const [start, end] = customDateRange.value;
  return !!(start && end);
});

const dateRange = computed(() => {
  if (hasSelectedCustomDateRange.value) return customDateRange.value;
  return dateRanges[dateRangeIndex.value];
});
const dateInterval = computed(() => {
  if (hasSelectedCustomDateRange.value)
    return getDateIntervalForCustomDateRange(customDateRange.value!);
  return dateIntervals[dateRangeIndex.value];
});

onMounted(async () => {
  apiState.value = ILoadingState.LOADING;

  try {
    await Promise.all([teamStore.fetchTeams()]);
  } catch {
    apiState.value = ILoadingState.ERROR;
  } finally {
    apiState.value = ILoadingState.IDLE;
  }
});

const filters = ref<ContactFilters>({
  customerPhoneQuery: "",
  ratingRange: [0, 1],
  teams: [],
  users: [],
});

const { contactsStats, loadingStats } = useContactsStats(filters, dateRange);
const { loadingTimeseries, contactsTimeseries } = useContactsTimeseries(
  dateRange,
  dateInterval,
  filters,
);

const { chartOptions, chartData } = useContactTimeseriesChart({
  contactsTimeseries,
  dateRange,
  dateInterval,
});

function onViewContactClicked(contactId: number) {
  router.push({
    name: "calls/call-history",
    query: { contactId: contactId.toString() },
  });
}

function setCustomDateFilter(
  v: Date | (Date | null)[] | Date[] | null | undefined,
) {
  customDateRange.value = v as [Date, Date];
}
</script>
